// frontend/src/inbox/wischRuecknahme.js
//
// Die ENTSCHEIDUNGEN der Wischaktionen beider Inboxen — rein, ohne Netz. Sie
// stehen hier und nicht in den zwei Kartenkomponenten, weil genau
// diese Entscheidungen die Regel G7 tragen („ein Wisch fuehrt nur aus, wenn es
// einen Widerruf gibt") und weil jsdom kein Layout hat: die GESTE traegt
// `frontend/tests/design/ui2-inbox-karten.spec.ts` bzw.
// `frontend/tests/po/po-inbox-mobil.spec.ts` ueber CDP, die ENTSCHEIDUNG
// traegt `frontend/src/inbox/wischRuecknahme.test.js`.
// ⚠️ „ohne React" gilt fuer den EIGENEN Quelltext; die Datei importiert seit
// dem 2026-10-09 die Richtungssperre aus `hooks/useSwipeRow.js` (s. unten), und
// dieses Modul ist ein Hook. Das ist der Preis dafuer, dass die Schwelle keine
// zweite Zahl ist.
//
// 🔴 Der Gestenvertrag G1–G15 steht in
// `docs/knowledge/design-language/pattern-library.md`.
//
// 🔴 Seit dem 2026-10-09 (R15 W2) liest diese Datei EINEN Wert aus dem
// Gestenhaken: die Richtungssperre. Damit haengt die Sichtbarkeitsschwelle des
// Feldes nicht mehr an einer zweiten, abgeschriebenen Zahl — siehe
// `WISCH_FELD_SICHTBAR_AB` unten.
import { SWIPE_DIRECTION_LOCK_PX } from '../hooks/useSwipeRow'

/**
 * Die Rueckwege der RMA-Inbox, gemessen am 2026-10-09 (Beleg je Zeile):
 *
 *   gelesen/ungelesen  — LOKAL. Der Zaehler liegt in `localStorage`
 *                        (`rma_inbox_seen`, `RMAInbox.jsx`), der Server kennt
 *                        ihn nicht. Der Widerruf ist der Umschalter selbst.
 *   archivieren        — `POST /api/rma/inbox/<id>/archive`
 *                        (`backend/rma_routes.py:39063`), Gegenrichtung
 *                        `…/unarchive` (`:39071`). Beide vorhanden.
 *   ignorieren         — `POST /api/rma/inbox/<id>/ignore` (`:39055`) setzt
 *                        `action_taken='ignored'`; eine Gegenrichtung gibt es
 *                        NICHT (`grep -rn "unignore" backend/` → leer).
 *   neu verarbeiten    — `POST /api/rma/inbox/<id>/reprocess` (`:39111`) ist eine
 *                        Neuanalyse, kein Widerruf.
 *   verknuepfen        — `POST /api/rma/inbox/<id>/link-case` (`:38877`),
 *                        Gegenrichtung `…/unlink-case` (`:39028`).
 *
 * Die drei ohne Widerruf stehen deshalb NUR im ⋯-Menue und werden getippt.
 */
export function wischArchivPlan({ istArchiviert }) {
  return {
    hin: istArchiviert ? 'unarchive' : 'archive',
    zurueck: istArchiviert ? 'archive' : 'unarchive',
    text: istArchiviert ? 'Unarchived' : 'Archived',
    fehler: istArchiviert ? 'Unarchive failed' : 'Archive failed',
    // 🔴 R16 W1 — der Rueckweg braucht SEINE Fehlermeldung. `fehler` gehoert
    // zum Hinweg (`hin`); wer sie fuer den Widerruf wiederverwendet, meldet die
    // verkehrte Handlung. Sie ist die Gegenrichtung von `fehler`, genau wie
    // `zurueck` die Gegenrichtung von `hin` ist.
    fehlerZurueck: istArchiviert ? 'Archive failed' : 'Unarchive failed',
  }
}

/** Gelesen ⇄ ungelesen. `zurueck` ist der Zustand VOR dem Wisch. */
export function wischGelesenPlan({ istGelesen }) {
  return {
    setze: !istGelesen,
    zurueck: istGelesen,
    text: istGelesen ? 'Marked unread' : 'Marked read',
  }
}

/**
 * 🔴 Die PO-Inbox fuehrt KEINEN Wisch aus, und das ist gemessen:
 *   ignorieren      `POST /api/purchase_orders/inbox/<id>/ignore`
 *                   (`backend/purchase_orders/routes.py:3544`) setzt
 *                   `is_archived` UND `action_taken='ignored'` — eine
 *                   Gegenrichtung gibt es nicht.
 *   neu verarbeiten `…/reprocess` (`:3526`) — Neuanalyse, kein Widerruf.
 *   verknuepfen     `…/link` (`:3461`) — die einzige Gegenrichtung waere
 *                   `DELETE /inbox/<id>` (`:3580`), also das Loeschen des
 *                   ganzen Belegs. Kein Widerruf.
 * Also deckt der Wisch nur AUF (G7 Satz 2): er schiebt die Kachel hervor,
 * ausgefuehrt wird getippt. Diese Konstante ist die eine Stelle, an der das
 * festgehalten ist; wer sie umdreht, baut einen Wisch ohne Widerruf.
 */
export const PO_WISCH_FUEHRT_AUS = false

/**
 * Die Kachel ist ein ZUSTAND, kein Ausloeser: aus der Ruhe deckt ein Zug seine
 * Seite auf, und **jeder weitere Zug schliesst sie wieder** — auch der
 * Gegenzug.
 *
 * 🔴 Die Hausform steht in der RMA-Fallkarte, und sie ist dort zweimal
 * ablesbar:
 *   `rma/RMACasesDashboardBody.jsx` › `swipeReleaseAction` —
 *   `if (finalX >= 0) return 'snapClose'`, also: **ein Zug in die
 *   Gegenrichtung ueber den Ursprung hinaus schliesst die Zeile**, er oeffnet
 *   nicht die andere Seite. Und `:1733-1735` (`snapClose`) ist der eine Weg,
 *   auf dem eine offene Zeile zur Ruhe kommt.
 *   `rma/RMACasesDashboardBody.jsx` › `handleCardClick` — ein Tipp
 *   auf die offene Karte schliesst sie ebenfalls, statt sie zu oeffnen.
 *
 * 🔴 BIS ZUM 2026-10-09 STAND HIER `aktuell === richtung ? null : richtung`.
 * Der Gegenzug wechselte damit auf die ANDERE Seite, statt zu schliessen: aus
 * `left` offen wurde bei einem Rechtszug `right` — die Kachel wanderte unter
 * dem Daumen von einer Kante zur anderen. Gemessen im Browser
 * (`frontend/tests/po/po-inbox-mobil.spec.ts`, P.2): Vordergrund −100 → +124,
 * die andere Kachel offen. Die Hausform kennt genau eine Gegenbewegung, und
 * sie heisst „zu".
 *
 * Warum auch die GLEICHE Richtung schliesst: die Hausform misst ihren Zug ab
 * dem Griff (`finalX`), ein kleiner Zug derselben Richtung liegt dort unter
 * der Einrastschwelle und schliesst ebenfalls. Diese Karte hat keine
 * Einrastschwelle — sie kennt nur „auf" und „zu" —, und ein Zug, der eine
 * bereits offene Kachel noch weiter aufziehen will, hat nichts zu tun.
 */
export function naechsteAufdeckung(aktuell, richtung) {
  return aktuell === null ? richtung : null
}

// ── Die Gestenparameter — EINE Stelle fuer BEIDE Karten (R16 W4) ─────────────
//
// 🔴 Bis zum 2026-10-09 stand `WISCH_FELD_SICHTBAR_AB` zweimal im Baum
// (`rma/RMAInbox.jsx`, `POInbox.jsx`), die Abschaltung aus G8 einmal benannt und
// einmal woertlich inline. Ein Gestenparameter gehoert zur GESTE, nicht zur
// Kartenform: zwei Kopien derselben Schwelle sind zwei Schwellen, sobald jemand
// eine davon anfasst — und die zwei Karten sollen sich gleich anfuehlen.

/** Ab welchem Versatz das Aktionsfeld sichtbar wird — **die Richtungssperre
 *  selbst**, nicht eine eigene Zahl.
 *
 *  🔴 Bis zum 2026-10-09 stand hier die `8`, und der Kommentar begruendete sie
 *  mit G4 („darunter ist die Geste noch nicht entschieden"). Die Zahl war eine
 *  ZWEITE Schwelle neben der Richtungssperre (10 px, `SWIPE_DIRECTION_LOCK_PX`,
 *  `hooks/useSwipeRow.js:79`). Ein Flackern hat sie NICHT erzeugt: `dx` wird
 *  nur hinter dem Achsen-Waechter gesetzt (`useSwipeRow.js:215`, `setDx` an
 *  `:219`), und die Achse rastet erst ab 10 px ein (`:211`) — BIS DAHIN ist
 *  `dx` 0, das Feld war vor der Entscheidung also nie sichtbar. Danach folgt
 *  `dx` jedem Zug: der einzige sichtbare Unterschied der zwei Zahlen ist der
 *  RUECKWEG zwischen 8 und 10 px — weicht der Finger von z. B. 12 auf 9 zurueck,
 *  liess die alte 8 das Feld stehen und die 10 nimmt es weg. Eine Fassung vom
 *  selben Tag behauptete das Flackern im Vorwaertsfenster; sie war gemessen
 *  falsch (R15-WV2, Bedingung 2).
 *
 *  Deshalb steht hier die Konstante des Hakens und keine zweite Zahl: wer die
 *  Sperre verschiebt, verschiebt die Sichtbarkeit mit. Die Hausgroessenordnung
 *  aus `NotificationCenter.jsx` (`Math.abs(dx) > 8`) ist damit bewusst
 *  aufgegeben — dort ist das Feld nur eine Farbflaeche, hier kuendigt es eine
 *  Handlung an. */
export const WISCH_FELD_SICHTBAR_AB = SWIPE_DIRECTION_LOCK_PX

/** G8 — die eine CSS-Transition der Wischkarte, unter `prefers-reduced-motion`
 *  abgeschaltet. `MotionConfig` (`App.js:213`) deckt nur framer-motion ab, nicht
 *  eine CSS-Transition; Hausform `rma/RMACasesDashboardBody.jsx` › `.caseHoverActions`.
 *
 *  Reines CSS, kein React — die Datei bleibt der react-freie Entscheidungsort. */
export const WISCH_BEWEGUNG_SX = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
}
